import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import StatusChip from '../../components/agent/StatusChip'
import { supabase } from '../../lib/supabase'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import { fullDate, todayIso } from '../reports/reportDates'
import { formatTimesInText, useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import { leaveRequestDisplayState } from './leaveRequestStore'
import {
  addAvailabilityRule,
  AVAILABILITY_KIND_LABEL,
  describeWindow,
  getMyAvailabilityExceptions,
  getMyAvailabilityRules,
  getMyShiftChoices,
  removeAvailabilityRule,
  resubmitAvailabilityException,
  submitAvailabilityException,
  WEEKDAY_NAMES,
  withdrawAvailabilityException,
  type AvailabilityException,
  type AvailabilityKind,
  type AvailabilityRule,
  type ShiftChoice,
  type WriteResult,
} from './availabilityStore'

const KINDS = Object.keys(AVAILABILITY_KIND_LABEL) as AvailabilityKind[]
// Monday first, the order a working week reads in.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

const fieldClass = 'min-h-11 w-full rounded-control border border-line-strong bg-canvas px-2.5 py-2 text-small'
const labelClass = 'mb-1 block text-caption font-bold text-ink-muted'
const buttonClass = 'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2'
const primaryButtonClass = 'flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover'

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

// A window in words, its times in the account's own format.
const windowLabel = (startTime: string | undefined, endTime: string | undefined, format: TimeFormat) =>
  formatTimesInText(describeWindow(startTime, endTime), format)

// Whether a window can be saved. An end before the start runs into the next
// day, so only a missing time or two equal ones are refused.
function windowError(wholeDay: boolean, start: string, end: string): string | null {
  if (wholeDay) return null
  if (!start || !end) return 'Choose a start time and an end time.'
  if (start === end) return 'Choose different start and end times. An end before the start runs into the next day.'
  return null
}

function Notice({ result }: { result: WriteResult | null }) {
  if (!result || result.ok) return null
  return (
    <p role="alert" className="text-caption font-semibold text-error">
      {result.message}
    </p>
  )
}

function WindowFields({
  idPrefix,
  shifts,
  wholeDay,
  onWholeDay,
  start,
  end,
  onStart,
  onEnd,
}: {
  idPrefix: string
  shifts: ShiftChoice[]
  wholeDay: boolean
  onWholeDay: (value: boolean) => void
  start: string
  end: string
  onStart: (value: string) => void
  onEnd: (value: string) => void
}) {
  // The shift the hours were taken from, until the hours are changed by hand.
  const [shiftId, setShiftId] = useState('')
  const { format } = useTimeFormat()

  function chooseShift(id: string) {
    setShiftId(id)
    const shift = shifts.find((candidate) => candidate.id === id)
    if (!shift) return
    onWholeDay(false)
    onStart(shift.startTime)
    onEnd(shift.endTime)
  }

  return (
    <>
      {shifts.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-shift`} className={labelClass}>
            Use a shift
          </label>
          <select id={`${idPrefix}-shift`} value={shiftId} onChange={(event) => chooseShift(event.target.value)} className={fieldClass}>
            <option value="">Choose a shift</option>
            {shifts.map((shift) => (
              <option key={shift.id} value={shift.id}>
                {shift.name}, {windowLabel(shift.startTime, shift.endTime, format)}
                {shift.own ? ' (your shift)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <label className="flex min-h-11 items-center gap-2 text-small font-semibold">
        <input
          type="checkbox"
          checked={wholeDay}
          onChange={(event) => {
            setShiftId('')
            onWholeDay(event.target.checked)
          }}
          className="size-5"
        />
        Whole day
      </label>
      {!wholeDay && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`${idPrefix}-start`} className={labelClass}>
                From
              </label>
              <input
                id={`${idPrefix}-start`}
                type="time"
                value={start}
                onChange={(event) => {
                  setShiftId('')
                  onStart(event.target.value)
                }}
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-end`} className={labelClass}>
                To
              </label>
              <input
                id={`${idPrefix}-end`}
                type="time"
                value={end}
                onChange={(event) => {
                  setShiftId('')
                  onEnd(event.target.value)
                }}
                className={fieldClass}
              />
            </div>
          </div>
          {start && end && end < start && <p className="text-caption text-ink-muted">Ends the next day.</p>}
        </>
      )}
    </>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`flex-none text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

// One weekday: its box opens to the day's rules and a form to add another,
// already set to that day.
function DayBox({ day, userId, rules, shifts, reload }: { day: number; userId: string; rules: AvailabilityRule[]; shifts: ShiftChoice[]; reload: () => void }) {
  const name = WEEKDAY_NAMES[day]
  const { format } = useTimeFormat()
  const panelId = `day-panel-${day}`
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<AvailabilityKind>('unavailable')
  const [wholeDay, setWholeDay] = useState(true)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [result, setResult] = useState<WriteResult | null>(null)

  const summary =
    rules.length === 0
      ? 'Nothing set'
      : rules.map((rule) => `${AVAILABILITY_KIND_LABEL[rule.kind]}, ${windowLabel(rule.startTime, rule.endTime, format)}`).join('; ')

  async function add(event: FormEvent) {
    event.preventDefault()
    const invalid = windowError(wholeDay, start, end)
    if (invalid) {
      setResult({ ok: false, message: invalid })
      return
    }
    const written = await addAvailabilityRule(userId, { weekday: day, kind, startTime: wholeDay ? undefined : start, endTime: wholeDay ? undefined : end })
    setResult(written)
    if (written.ok) reload()
  }

  async function remove(id: string) {
    const removed = await removeAvailabilityRule(userId, id)
    setResult(removed)
    if (removed.ok) reload()
  }

  return (
    <li className="rounded-surface border border-line">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-14 w-full items-center justify-between gap-3 rounded-surface p-3 text-left hover:bg-surface-2"
      >
        <span>
          <span className="block text-small font-bold">{name}</span>
          <span className="mt-0.5 block text-small text-ink-muted">{summary}</span>
        </span>
        <ChevronIcon open={open} />
      </button>

      <div id={panelId} hidden={!open} className="flex flex-col gap-3 border-t border-line p-3">
        {rules.length > 0 && (
          <ul className="flex flex-col gap-1">
            {rules.map((rule) => (
              <li key={rule.id} className="flex items-center justify-between gap-2">
                <span className="text-small">
                  <span className="font-bold">{AVAILABILITY_KIND_LABEL[rule.kind]}</span>, {windowLabel(rule.startTime, rule.endTime, format)}
                </span>
                <button
                  type="button"
                  onClick={() => remove(rule.id)}
                  aria-label={`Remove ${AVAILABILITY_KIND_LABEL[rule.kind].toLowerCase()} ${windowLabel(rule.startTime, rule.endTime, format).toLowerCase()} on ${name}`}
                  className="flex min-h-11 items-center px-2 text-small font-bold text-primary"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={add} aria-label={`Add availability for ${name}`} className="flex flex-col gap-3 rounded-surface bg-surface-2 p-3">
          <div>
            <label htmlFor={`rule-kind-${day}`} className={labelClass}>
              Availability
            </label>
            <select id={`rule-kind-${day}`} value={kind} onChange={(event) => setKind(event.target.value as AvailabilityKind)} className={fieldClass}>
              {KINDS.map((option) => (
                <option key={option} value={option}>
                  {AVAILABILITY_KIND_LABEL[option]}
                </option>
              ))}
            </select>
          </div>
          <WindowFields idPrefix={`rule-${day}`} shifts={shifts} wholeDay={wholeDay} onWholeDay={setWholeDay} start={start} end={end} onStart={setStart} onEnd={setEnd} />
          <Notice result={result} />
          <button type="submit" className={primaryButtonClass}>
            Add
          </button>
        </form>
      </div>
    </li>
  )
}

function WeeklyAvailability({ userId, rules, shifts, reload }: { userId: string; rules: AvailabilityRule[]; shifts: ShiftChoice[]; reload: () => void }) {
  return (
    <section aria-labelledby="weekly-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="weekly-heading" className="text-h4 font-extrabold">
          Your week
        </h2>
        <p className="mt-0.5 text-small text-ink-muted">When you can and cannot work each week. Choose a day to set it. A day with nothing set is open.</p>
      </div>

      <ul className="flex flex-col gap-2">
        {WEEK_ORDER.map((day) => (
          <DayBox key={day} day={day} userId={userId} rules={rules.filter((rule) => rule.weekday === day)} shifts={shifts} reload={reload} />
        ))}
      </ul>
    </section>
  )
}

function Exceptions({ userId, exceptions, shifts, reload }: { userId: string; exceptions: AvailabilityException[]; shifts: ShiftChoice[]; reload: () => void }) {
  const today = todayIso()
  const { format } = useTimeFormat()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [kind, setKind] = useState<AvailabilityKind>('unavailable')
  const [wholeDay, setWholeDay] = useState(true)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<WriteResult | null>(null)

  function clearForm() {
    setEditingId(null)
    setStartDate('')
    setEndDate('')
    setKind('unavailable')
    setWholeDay(true)
    setStart('')
    setEnd('')
    setReason('')
  }

  function edit(exception: AvailabilityException) {
    setEditingId(exception.id)
    setStartDate(exception.startDate)
    setEndDate(exception.endDate)
    setKind(exception.kind)
    setWholeDay(!exception.startTime)
    setStart(exception.startTime ?? '')
    setEnd(exception.endTime ?? '')
    setReason(exception.reason ?? '')
    setResult(null)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!startDate || !endDate || endDate < startDate) {
      setResult({ ok: false, message: 'Choose a start date that is on or before the end date.' })
      return
    }
    if (startDate < today) {
      setResult({ ok: false, message: 'Choose dates from today onward.' })
      return
    }
    const invalid = windowError(wholeDay, start, end)
    if (invalid) {
      setResult({ ok: false, message: invalid })
      return
    }
    const exception = {
      startDate,
      endDate,
      kind,
      startTime: wholeDay ? undefined : start,
      endTime: wholeDay ? undefined : end,
      reason,
    }
    const written = editingId ? await resubmitAvailabilityException(userId, editingId, exception) : await submitAvailabilityException(userId, exception)
    setResult(written)
    if (written.ok) {
      clearForm()
      reload()
    }
  }

  async function withdraw(id: string) {
    const withdrawn = await withdrawAvailabilityException(id)
    setResult(withdrawn)
    if (withdrawn.ok) {
      if (editingId === id) clearForm()
      reload()
    }
  }

  return (
    <section aria-labelledby="exceptions-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="exceptions-heading" className="text-h4 font-extrabold">
          Exceptions
        </h2>
        <p className="mt-0.5 text-small text-ink-muted">A change for particular dates. Your supervisor approves each one.</p>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3 rounded-surface border border-line bg-surface-2 p-3.5">
        <h3 className="text-small font-extrabold">{editingId ? 'Edit and resubmit' : 'Add an exception'}</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="exception-start-date" className={labelClass}>
              Start date
            </label>
            <input id="exception-start-date" type="date" min={today} value={startDate} onChange={(event) => setStartDate(event.target.value)} className={fieldClass} />
          </div>
          <div>
            <label htmlFor="exception-end-date" className={labelClass}>
              End date
            </label>
            <input id="exception-end-date" type="date" min={today} value={endDate} onChange={(event) => setEndDate(event.target.value)} className={fieldClass} />
          </div>
        </div>
        <div>
          <label htmlFor="exception-kind" className={labelClass}>
            Availability
          </label>
          <select id="exception-kind" value={kind} onChange={(event) => setKind(event.target.value as AvailabilityKind)} className={fieldClass}>
            {KINDS.map((option) => (
              <option key={option} value={option}>
                {AVAILABILITY_KIND_LABEL[option]}
              </option>
            ))}
          </select>
        </div>
        <WindowFields idPrefix="exception" shifts={shifts} wholeDay={wholeDay} onWholeDay={setWholeDay} start={start} end={end} onStart={setStart} onEnd={setEnd} />
        <div>
          <label htmlFor="exception-reason" className={labelClass}>
            Reason (optional)
          </label>
          <input id="exception-reason" type="text" maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} className={fieldClass} />
        </div>
        <Notice result={result} />
        <div className="flex gap-2">
          <button type="submit" className={primaryButtonClass}>
            {editingId ? 'Resubmit' : 'Submit'}
          </button>
          {editingId && (
            <button type="button" onClick={clearForm} className={buttonClass}>
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {exceptions.length === 0 ? (
        <p className="text-small text-ink-muted">No exceptions yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {exceptions.map((exception) => {
            const dates = exception.startDate === exception.endDate ? fullDate(exception.startDate) : `${fullDate(exception.startDate)} to ${fullDate(exception.endDate)}`
            return (
              <li key={exception.id} className="rounded-surface border border-line p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-small font-bold">{dates}</p>
                    <p className="text-small">
                      {AVAILABILITY_KIND_LABEL[exception.kind]}, {windowLabel(exception.startTime, exception.endTime, format)}
                    </p>
                    {exception.reason && <p className="text-caption text-ink-muted">{exception.reason}</p>}
                  </div>
                  <StatusChip state={leaveRequestDisplayState(exception.state)} />
                </div>
                {exception.state === 'returned' && exception.decisionComment && (
                  <p className="mt-2 text-small">
                    <span className="font-bold">Returned: </span>
                    {exception.decisionComment}
                  </p>
                )}
                {exception.state === 'denied' && exception.decisionComment && (
                  <p className="mt-2 text-small">
                    <span className="font-bold">Denied: </span>
                    {exception.decisionComment}
                  </p>
                )}
                {exception.state === 'pending' && (
                  <button type="button" onClick={() => withdraw(exception.id)} className="mt-1 flex min-h-11 items-center text-small font-bold text-primary">
                    Withdraw
                    <span className="sr-only"> {dates}</span>
                  </button>
                )}
                {exception.state === 'returned' && (
                  <button type="button" onClick={() => edit(exception)} className="mt-1 flex min-h-11 items-center text-small font-bold text-primary">
                    Edit and resubmit
                    <span className="sr-only"> {dates}</span>
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

export default function AgentAvailabilityPage() {
  const identity = useProfileIdentity()
  const [userId, setUserId] = useState<string | null>(null)
  const [rules, setRules] = useState<AvailabilityRule[]>([])
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([])
  const [shifts, setShifts] = useState<ShiftChoice[]>([])

  const load = useCallback(async (id: string) => {
    const [loadedRules, loadedExceptions, loadedShifts] = await Promise.all([getMyAvailabilityRules(id), getMyAvailabilityExceptions(id), getMyShiftChoices(id)])
    setRules(loadedRules)
    setExceptions(loadedExceptions)
    setShifts(loadedShifts)
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id || cancelled) return
      setUserId(id)
      await load(id)
    })
    return () => {
      cancelled = true
    }
  }, [load])

  const reload = useCallback(() => {
    if (userId) void load(userId)
  }, [userId, load])

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#availability-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="availability-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4 lg:p-8">
          <Link to="/agent" className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted">
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Your Availability</h1>
            <p className="mt-0.5 text-small text-ink-muted">Your supervisor sees this when building the schedule.</p>
          </div>

          {userId && <WeeklyAvailability userId={userId} rules={rules} shifts={shifts} reload={reload} />}
          {userId && <Exceptions userId={userId} exceptions={exceptions} shifts={shifts} reload={reload} />}
        </div>
      </main>
    </div>
  )
}
